import React, { useState, useEffect, useCallback } from 'react';
import './profile.scss';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/navbar/Navbar';
import Sidebar from '../../components/sidebar/Sidebar';
import Loading from '../../components/Loading/Loading';
import { sendPasswordResetEmail } from 'firebase/auth';
// Shareholder records live in PostgreSQL — pending list and approvals go
// through the backend API, NOT Firestore (which holds the stale
// pre-migration copy)
import dbService from '../../services/database';
import {
  avatarColor,
  getInitials,
  roleBadgeClass,
  roleLabel,
} from '../../utils/ui';

const Profile = ({ isReadOnly, userRole }) => {
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // State to track selected users
  const [staffs, setStaffs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [cardFilter, setCardFilter] = useState('');
  const itemsPerPage = 10;

  // Clear the initial loading gate once mounted; staff and pending lists
  // load via their own effects below.
  useEffect(() => {
    setIsLoading(false);
  }, []);

  // Pending shareholders come from PostgreSQL via the backend API
  const fetchPendingUsers = useCallback(async () => {
    const { data, error } = await dbService.getUsers({
      status: 'pending',
      limit: 100,
      orderBy: 'created_at',
      order: 'desc',
    });

    if (error) {
      console.error('Error fetching pending shareholders:', error);
      return;
    }
    setPendingUsers(data);
  }, []);

  useEffect(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  // Staff list stays on Firestore — it is the source of truth for staff
  // roles (readable by staff, writable by no client, per security rules)
  useEffect(() => {
    const staffsQuery = query(collection(db, 'staff'));
    const staffUnsubscribe = onSnapshot(staffsQuery, (snapshot) => {
      setStaffs(
        snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      );
    });

    return () => {
      staffUnsubscribe();
    };
  }, []);

  // PostgreSQL column names (display_name, membership_code, ghana_card),
  // with null-safe matching so members missing a phone/card still display
  const filteredPendingUsers = pendingUsers.filter((user) => {
    const term = searchTerm.toLowerCase();
    const name = (user.display_name || user.displayName || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const code = (user.membership_code || user.membershipCode || '').toLowerCase();
    const phone = (user.phone || '').toLowerCase();
    const card = (user.ghana_card || user.card || '').toLowerCase();

    return (
      (name.includes(term) || email.includes(term) || code.includes(term)) &&
      phone.includes(phoneFilter.toLowerCase()) &&
      card.includes(cardFilter.toLowerCase())
    );
  });

  const displayedPendingUsers = filteredPendingUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePasswordReset = (email) => {
    if (!hasPermission('reset-password')) {
      alert("You don't have permission to reset passwords");
      return;
    }

    sendPasswordResetEmail(auth, email)
      .then(() => {
        alert('Password reset email sent!');
      })
      .catch((error) => {
        console.error('Error resetting password:', error);
      });
  };

  // Approval writes to PostgreSQL via the backend API. The backend
  // additionally enforces that only a super-user may change status.
  const approveUser = async (userId) => {
    await dbService.updateUser(userId, { status: 'approved' });
  };

  const handleApproveUser = async (userId) => {
    if (!hasPermission('approve-users')) {
      alert("You don't have permission to approve users");
      return;
    }

    try {
      setIsApproving(true);
      await approveUser(userId);
      alert('User approved successfully.');
    } catch (error) {
      console.error('Error approving user:', error);
      alert('Failed to approve user. Please try again.');
    } finally {
      setIsApproving(false);
      fetchPendingUsers();
    }
  };

  const handleApproveSelectedUsers = async () => {
    if (!hasPermission('bulk-approve')) {
      alert("You don't have permission to approve users");
      return;
    }

    try {
      setIsApproving(true);
      const results = await Promise.allSettled(selectedUsers.map(approveUser));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        alert(`${results.length - failed} approved, ${failed} failed. Please retry the rest.`);
      } else {
        alert(`${results.length} user(s) approved successfully.`);
      }
    } finally {
      setIsApproving(false);
      setSelectedUsers([]);
      fetchPendingUsers();
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId)
        : [...prevSelected, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === displayedPendingUsers.length) {
      setSelectedUsers([]); // Deselect all if already selected
    } else {
      setSelectedUsers(displayedPendingUsers.map((user) => user.id)); // Select all users on the current page
    }
  };

  const handleAddUser = () => {
    navigate('/add-user');
  };

  // Function to check if user has permission for specific actions
  const hasPermission = (action) => {
    // Audit and compliance roles are always read-only
    if (['audit', 'compliance'].includes(userRole)) {
      return false;
    }

    switch (action) {
      case 'reset-password':
        return userRole === 'super-user';
      case 'approve-users':
        return userRole === 'super-user';
      case 'add-user':
        return userRole === 'super-user';
      case 'bulk-approve':
        return userRole === 'super-user';
      default:
        return false;
    }
  };

  if (isLoading) {
    return <Loading inline={true} message="Loading profile data..." />;
  }

  return (
    <div className="profile">
      <Sidebar />
      <div className="profileContainer">
        <Navbar />
        <div className="profileContent">
          <div className="pageHead">
            <div>
              <h1>Administration</h1>
              <p>Manage staff accounts and review pending shareholder applications.</p>
            </div>
            {hasPermission('add-user') && (
              <button onClick={handleAddUser} className="addUserButton">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add User
              </button>
            )}
          </div>

          {isReadOnly && (
            <div className="readOnlyBanner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              You are in read-only mode ({roleLabel(userRole)}) — changes are disabled.
            </div>
          )}

          <div className="card adminsSection">
            <div className="sectionHead">
              <span className="sectionIcon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </span>
              <h2>Staff</h2>
              <span className="countPill">{staffs.length}</span>
            </div>
            <div className="tableWrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th className="right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {staffs.map((staff) => (
                    <tr key={staff.id}>
                      <td>
                        <div className="identity">
                          <span
                            className="avatar"
                            style={{ background: avatarColor(staff.id || staff.email) }}
                            aria-hidden="true"
                          >
                            {getInitials(staff.name)}
                          </span>
                          <div className="identityText">
                            <span className="primary">{staff.name}</span>
                            <span className="secondary">{staff.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${roleBadgeClass(staff.role)}`}>
                          {roleLabel(staff.role)}
                        </span>
                      </td>
                      <td className="right">
                        {hasPermission('reset-password') && (
                          <button
                            className="btnSecondary"
                            onClick={() => handlePasswordReset(staff.email)}
                            disabled={isReadOnly}
                          >
                            Reset Password
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card searchSection">
            <label htmlFor="pendingSearch" className="searchLabel">
              Search pending shareholders
            </label>
            <div className="searchWrap">
              <span className="searchIcon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                id="pendingSearch"
                type="text"
                placeholder="Search by name, code or email…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="searchField"
              />
            </div>
          </div>

          <div className="card usersSection">
            <div className="sectionHead">
              <span className="sectionIcon amber" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </span>
              <h2>Pending Shareholders</h2>
              <span className="countPill">{filteredPendingUsers.length}</span>
            </div>

            {filteredPendingUsers.length === 0 ? (
              <div className="emptyState">
                <span className="emptyIcon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </span>
                <span className="emptyTitle">No pending shareholders</span>
                <p>Everyone matching your filters has been reviewed.</p>
              </div>
            ) : (
              <>
                <div className="tableWrap">
                  <table>
                    <thead>
                      <tr>
                        <th className="checkCol">
                          <input
                            type="checkbox"
                            aria-label="Select all on this page"
                            checked={
                              displayedPendingUsers.length > 0 &&
                              selectedUsers.length === displayedPendingUsers.length
                            }
                            onChange={handleSelectAllUsers}
                          />
                        </th>
                        <th>Shareholder</th>
                        <th>
                          Contact Number
                          <div className="columnFilter">
                            <input
                              type="text"
                              placeholder="Filter…"
                              value={phoneFilter}
                              onChange={(e) => setPhoneFilter(e.target.value)}
                              className="filterInput"
                            />
                          </div>
                        </th>
                        <th>
                          Ghana Card Number
                          <div className="columnFilter">
                            <input
                              type="text"
                              placeholder="Filter…"
                              value={cardFilter}
                              onChange={(e) => setCardFilter(e.target.value)}
                              className="filterInput"
                            />
                          </div>
                        </th>
                        <th className="right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedPendingUsers.map((user) => {
                        const name = user.display_name || user.displayName || 'Unknown';
                        const card = user.ghana_card || user.card;
                        return (
                          <tr key={user.id}>
                            <td className="checkCol">
                              <input
                                type="checkbox"
                                aria-label={`Select ${name}`}
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => handleSelectUser(user.id)}
                              />
                            </td>
                            <td>
                              <div className="identity">
                                <span
                                  className="avatar"
                                  style={{ background: avatarColor(user.id || name) }}
                                  aria-hidden="true"
                                >
                                  {getInitials(name)}
                                </span>
                                <div className="identityText">
                                  <span className="primary">{name}</span>
                                  {user.membership_code && (
                                    <span className="secondary">{user.membership_code}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="mono">{user.phone || <span className="muted">—</span>}</td>
                            <td>
                              {card ? (
                                <span className="mono">{card}</span>
                              ) : (
                                <span className="badge badge--muted">Not provided</span>
                              )}
                            </td>
                            <td className="right">
                              {hasPermission('approve-users') && (
                                <button
                                  className="btnApprove"
                                  onClick={() => handleApproveUser(user.id)}
                                  disabled={isApproving || user.status === 'approved'}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                  {user.status === 'approved' ? 'Approved' : 'Approve'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="tableFooter">
                  {hasPermission('bulk-approve') && (
                    <button
                      className="btnPrimary"
                      onClick={handleApproveSelectedUsers}
                      disabled={isApproving || selectedUsers.length === 0}
                    >
                      {isApproving
                        ? 'Approving…'
                        : `Approve Selected${selectedUsers.length ? ` (${selectedUsers.length})` : ''}`}
                    </button>
                  )}

                  <div className="pagination">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      ←
                    </button>
                    <span>
                      {currentPage} / {Math.max(1, Math.ceil(filteredPendingUsers.length / itemsPerPage))}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentPage((p) =>
                          Math.min(p + 1, Math.ceil(filteredPendingUsers.length / itemsPerPage))
                        )
                      }
                      disabled={
                        currentPage >= Math.ceil(filteredPendingUsers.length / itemsPerPage)
                      }
                      aria-label="Next page"
                    >
                      →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
